/**
 * useLayoutStyle - a reusable hook to build memoized style objects for layout
 * components (flex/grid/wrap). Centralizing this reduces duplicated useMemo
 * logic across components and minimizes transient allocations.
 *
 * Returns a React.CSSProperties object suitable for passing to a `style` prop.
 */
import * as React from 'react'
import { AlignItemsEnum, JustifyContentEnum, OverflowEnum } from './LayoutXenum'

export type UseLayoutStyleParams = {
  display?: 'flex' | 'grid'
  crossAxisAlignment?: AlignItemsEnum
  direction?: 'row' | 'column'
  fullWidth?: boolean
  gap?: string
  colGap?: string
  rowGap?: string
  gridTemplate?: string
  height?: string
  mainAxisAlignment?: JustifyContentEnum
  overflow?: OverflowEnum
  // Use simpler string types for verticalAlign/wordBreak to avoid
  // potential mismatches with the project's React CSSProperties typing.
  verticalAlign?: string
  wordBreak?: string
}

/**
 * Build a memoized style object for layout components.
 * Only include properties when defined to avoid passing falsy values
 * like `false` to CSS properties.
 */
export function useLayoutStyle(params: UseLayoutStyleParams) {
  const {
    display,
    crossAxisAlignment,
    direction,
    fullWidth,
    gap,
    colGap,
    rowGap,
    gridTemplate,
    height,
    mainAxisAlignment,
    overflow,
    verticalAlign,
    wordBreak,
  } = params

  return React.useMemo<React.CSSProperties>(() => {
    // Use a loose shape for the style object during construction to
    // avoid strict property checks against the project's CSSProperties.
    const style: any = {}

    if (crossAxisAlignment !== undefined) style.alignItems = crossAxisAlignment

    if (display === 'flex') {
      style.display = 'flex'
      // If explicit col/row gaps provided, combine them into gap for flex wrap
      if (colGap !== undefined || rowGap !== undefined) {
        const cg = colGap ?? '0px'
        const rg = rowGap ?? '0px'
        style.gap = `${cg} ${rg}`
      } else if (gap !== undefined) {
        style.gap = gap
      }

      // direction may be used as flexDirection or flexFlow depending on caller
      if (direction) style.flexDirection = direction
    } else if (display === 'grid') {
      style.display = 'grid'
      if (gap !== undefined) style.gap = gap
      if (direction === 'row' && gridTemplate !== undefined)
        style.gridTemplateColumns = gridTemplate
      if (direction === 'column' && gridTemplate !== undefined)
        style.gridTemplateRows = gridTemplate
    }

    if (height !== undefined) style.height = height
    if (mainAxisAlignment !== undefined) style.justifyContent = mainAxisAlignment
    if (overflow !== undefined) style.overflow = overflow
    if (verticalAlign !== undefined) style.verticalAlign = verticalAlign
    if (wordBreak !== undefined) style.wordBreak = wordBreak

    style.width = fullWidth ? '100%' : 'auto'

    return style as React.CSSProperties
  }, [
    display,
    crossAxisAlignment,
    direction,
    fullWidth,
    gap,
    colGap,
    rowGap,
    gridTemplate,
    height,
    mainAxisAlignment,
    overflow,
    verticalAlign,
    wordBreak,
  ])
}

export default useLayoutStyle
